import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Field, Badge, SectionTitle } from "@/components/ui-hud";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plane, Users2, DollarSign, Loader2, ArrowRight, Globe2, CheckCircle2, Star } from "lucide-react";

export default function CharterRequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [bidForm, setBidForm] = useState({ aircraft_id: "", price_usd: "", notes: "", includes: [] });
  const [busy, setBusy] = useState(false);
  const isOperator = user?.role === "charter_operator";

  const load = async () => {
    const r = await api.get(`/charter-requests/${id}`);
    setData(r.data);
  };
  useEffect(() => {
    load();
    if (user?.role === "charter_operator") {
      api.get("/operators/me/fleet").then((r) => setFleet(r.data.fleet || [])).catch(() => {});
    }
  }, [id, user?.role]);

  if (!data) return <div className="max-w-5xl mx-auto px-6 py-16"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…</div>;
  const { request, bids } = data;

  const submitBid = async () => {
    if (!bidForm.aircraft_id || !bidForm.price_usd) return toast.error("Aircraft + price required");
    setBusy(true);
    try {
      await api.post(`/charter-requests/${id}/bids`, {
        request_id: id,
        aircraft_id: bidForm.aircraft_id,
        price_usd: Number(bidForm.price_usd),
        notes: bidForm.notes,
        includes: bidForm.includes,
      });
      toast.success("Bid submitted");
      setBidForm({ aircraft_id: "", price_usd: "", notes: "", includes: [] });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bid failed");
    } finally { setBusy(false); }
  };

  const award = async (bidId) => {
    if (!window.confirm("Award this bid? This closes the request.")) return;
    try {
      await api.post(`/charter-requests/${id}/award/${bidId}`);
      toast.success("Bid awarded");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Award failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <SectionTitle
        overline={`// CHARTER REQUEST · ${request.status.toUpperCase()}`}
        title={request.routing?.join(" → ") || `${request.departure_icao} → ${request.destination_icao}`}
      />

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          <Card>
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
              <div className="flex flex-wrap gap-2 items-center">
                {request.is_international && (
                  <span className="mono text-[10px] px-2 py-0.5 rounded-full border border-orange-hud/40 text-orange-hud bg-orange-hud/10 inline-flex items-center gap-1"><Globe2 className="h-3 w-3" /> INTL</span>
                )}
                <Badge tone="cyan">{request.aircraft_class_pref || "Any class"}</Badge>
                <Badge tone="mono">{request.passengers} pax</Badge>
              </div>
              {request.status === "awarded" && <Badge tone="cyan" pulse>AWARDED</Badge>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Start" v={request.trip_start} />
              {request.trip_end && <Stat label="End" v={request.trip_end} />}
              <Stat label="Budget" v={request.budget_usd ? `$${request.budget_usd.toLocaleString()}` : "—"} />
              <Stat label="Pets" v={request.pets ? "Yes" : "No"} />
            </div>
            {request.catering && <Line label="Catering" v={request.catering} />}
            {request.special_requests && <Line label="Notes" v={request.special_requests} />}
            <div className="mt-4 pt-3 border-t border-white/[0.05] text-xs text-white/50 mono">
              Requested by {request.contact_name} · {request.contact_email}{request.contact_phone ? ` · ${request.contact_phone}` : ""}
            </div>
          </Card>

          {/* Bid list */}
          <div>
            <div className="overline text-cyan mb-3">// BIDS · {bids.length}</div>
            {bids.length === 0 && <Card><div className="text-center py-6 text-white/50 mono">Waiting for operator bids…</div></Card>}
            <div className="space-y-3">
              {bids.map((b) => (
                <Card key={b.id} testId={`bid-${b.id}`} className={`border ${request.awarded_bid_id === b.id ? "border-avail" : "border-cyan/20"}`}>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="flex-1">
                      <div className="font-display text-lg font-semibold tracking-tight">{b.operator_company || "Operator"}</div>
                      <div className="mono text-xs text-white/60">{b.aircraft_snapshot?.tail_number} · {b.aircraft_snapshot?.type_code}{b.aircraft_snapshot?.year ? ` · ${b.aircraft_snapshot.year}` : ""}</div>
                    </div>
                    <div className="mono text-2xl text-cyan font-bold">${b.price_usd.toLocaleString()}</div>
                    {request.status === "open" && (
                      <button onClick={() => award(b.id)} data-testid={`award-${b.id}`} className="btn btn-primary" style={{background:"#2ED573", borderColor:"#2ED573"}}>
                        <Star className="h-3.5 w-3.5" /> Award
                      </button>
                    )}
                    {request.awarded_bid_id === b.id && <Badge tone="cyan"><CheckCircle2 className="h-3 w-3 inline mr-1" /> WON</Badge>}
                  </div>
                  {b.includes?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {b.includes.map((inc) => <span key={inc} className="mono text-[10px] px-2 py-0.5 rounded-full border border-cyan/30 text-cyan">{inc}</span>)}
                    </div>
                  )}
                  {b.notes && <div className="mt-2 text-sm text-white/70">{b.notes}</div>}
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Operator bid form */}
        <aside>
          {request.status !== "open" ? (
            <Card><div className="text-sm text-white/60 mono">This request is {request.status}. No new bids.</div></Card>
          ) : isOperator ? (
            <Card testId="bid-form">
              <div className="overline text-cyan mb-3">// SUBMIT YOUR BID</div>
              {fleet.length === 0 ? (
                <div className="text-sm text-white/60">Add aircraft to your fleet first. <Link to="/operators/onboard" className="text-cyan underline">Onboard →</Link></div>
              ) : (
                <>
                  <Field label="Aircraft">
                    <select data-testid="bid-aircraft" className="input" value={bidForm.aircraft_id} onChange={(e) => setBidForm({...bidForm, aircraft_id: e.target.value})}>
                      <option value="">Choose from your fleet…</option>
                      {[...fleet]
                        .sort((a, b) => (a.tail_number || "").localeCompare(b.tail_number || ""))
                        .map((ac) => <option key={ac.id} value={ac.id}>{ac.tail_number} · {ac.type_code}</option>)}
                    </select>
                  </Field>
                  <Field label="Price (USD)">
                    <input data-testid="bid-price" type="number" className="input mono" value={bidForm.price_usd} onChange={(e) => setBidForm({...bidForm, price_usd: e.target.value})} placeholder="45000" />
                  </Field>
                  <Field label="Includes (comma-separated)">
                    <input data-testid="bid-includes" className="input" value={bidForm.includes.join(", ")} onChange={(e) => setBidForm({...bidForm, includes: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})} placeholder="Catering · Ground transport · WiFi" />
                  </Field>
                  <Field label="Notes">
                    <textarea data-testid="bid-notes" className="input" rows={3} value={bidForm.notes} onChange={(e) => setBidForm({...bidForm, notes: e.target.value})} placeholder="Anything else the customer should know" />
                  </Field>
                  <button onClick={submitBid} disabled={busy} data-testid="btn-submit-bid" className="btn btn-primary w-full justify-center">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Submit Bid <ArrowRight className="h-3.5 w-3.5" /></>}
                  </button>
                </>
              )}
            </Card>
          ) : (
            <Card>
              <div className="overline text-cyan mb-2">// ARE YOU AN OPERATOR?</div>
              <p className="text-sm text-white/70 mb-3">Sign in as a charter operator to submit a bid on this request.</p>
              <Link to="/operators/onboard" className="btn btn-primary">Become an Operator</Link>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, v }) { return <div><div className="eyebrow-muted mb-0.5">{label}</div><div className="mono text-cyan text-sm">{v}</div></div>; }
function Line({ label, v }) { return <div className="mt-3 pt-3 border-t border-white/[0.05]"><div className="eyebrow-muted mb-1">{label}</div><div className="text-sm text-white/85 whitespace-pre-wrap">{v}</div></div>; }
